import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Play, RotateCcw, CheckCircle2, AlertTriangle, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const ICON = { done: CheckCircle2, failed: AlertTriangle };

// One-off data changes (admin only). Startup never changes data — every change runs from here, once, with a backup.
export default function MigrationsPage() {
  const { api } = useAuth();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    try { setList((await api.get('/admin/migrations')).data); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!list.some(m => m.status === 'running')) return undefined;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [list, load]);
  const act = async (m, verb) => {
    const msg = verb === 'run'
      ? `Run "${m.title}" now? A full backup is taken first. Catalogue edits are paused while it runs (a few minutes).`
      : `Undo "${m.title}"? The catalogue (rates, option lists, recipes, roof types) is restored from the backup. Catalogue edits made since the run are lost.`;
    if (!window.confirm(msg)) return;
    setBusy(m.key + verb);
    try { await api.post(`/admin/migrations/${m.key}/${verb}`); toast.success(verb === 'run' ? 'Started' : 'Restored from backup'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(''); load();
  };
  return (
    <div data-testid="migrations-page" className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Migrations</h1>
        <p className="text-sm text-slate-500">One-off data changes. Each runs once, takes a backup first and can be undone. Restarts and deploys never change data.</p>
      </div>
      {list.map(m => (
        <div key={m.key} data-testid={`migration-${m.key}`} className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-slate-900">{m.title}</p>
              <p className="text-xs text-slate-500" data-testid={`migration-${m.key}-status`}>Status: <b>{m.status}</b>{m.finished_at ? ` · finished ${new Date(m.finished_at).toLocaleString()}` : ''}</p>
            </div>
            <div className="flex gap-2">
              {['not_run', 'failed', 'undone'].includes(m.status) && (
                <button data-testid={`migration-${m.key}-run`} disabled={!!busy} onClick={() => act(m, 'run')}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#D35400] text-white rounded text-sm font-semibold disabled:opacity-50">
                  {busy === m.key + 'run' ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} {m.status === 'failed' ? 'Resume' : 'Run'}
                </button>
              )}
              {['done', 'failed'].includes(m.status) && (
                <button data-testid={`migration-${m.key}-undo`} disabled={!!busy} onClick={() => act(m, 'undo')}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-white ring-1 ring-slate-300 rounded text-sm font-semibold text-slate-700">
                  <RotateCcw size={14} /> Undo
                </button>
              )}
            </div>
          </div>
          <ol className="space-y-1">
            {m.steps.map((st, i) => {
              const Ic = ICON[st.status] || (m.status === 'running' ? Loader2 : Circle);
              return (
                <li key={st.name} data-testid={`migration-${m.key}-step-${i}`} className="text-xs">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700">
                    <Ic size={12} className={st.status === 'done' ? 'text-emerald-600' : st.status === 'failed' ? 'text-rose-600' : 'text-slate-400'} /> {i + 1}. {st.name}
                  </span>
                  {st.error && <span className="ml-2 text-rose-700">{st.error}</span>}
                  {st.result && (
                    <pre className="mt-1 ml-5 bg-slate-50 rounded p-2 text-[10px] text-slate-600 overflow-x-auto max-h-48">{JSON.stringify(st.result, null, 1)}</pre>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </div>
  );
}
